import { useAppSelector } from '@/hooks';
import { Button, Tag } from 'antd';
import { entries, filter, isArray, isNil } from 'lodash';
import { useMemo } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FormBaseFilter from '@/components/base-filter';

import useAuthorization from '@/hooks/useAuthorization';
import useCompany from '@/hooks/useCompany';
import useFetch from '@/hooks/useFetch';
import useMotPic from '@/hooks/useMotPic';
import useOptionsGlobal from '@/hooks/useOptionsGlobal';

import { API } from '@/utils/constants/Apis';
import { DataViewer } from '@/utils/helpers/common';
import { formatDateTime } from '@/utils/helpers/globalHelper';
import { ICategory } from '@/utils/interfaces/template';

import PrimaryFormFilter from './PrimaryFormFilter';
import { DEFAULT_FILTER_FORM, DEFAULT_FILTER_FORM_EXTERNAL, FILTER_TASK_TYPE_OPTIONS, TASK_LABEL, TYPE_OF_TASK } from './constant';
import { IFormFilter, IPrimary } from './models';

export const getTotalFiltered = (watchPrimaryFields: IPrimary): number => {
  const keyNoNeedCount = 'isDraft';
  return filter(
    entries(watchPrimaryFields),
    ([key, value]) => !isNil(value) && value !== '' && key !== keyNoNeedCount && (!isArray(value) || value.length > 0)
  ).length;
};

export interface ITableHeaderProps {
  onSubmit: (values: FieldValues, isChangeView?: boolean) => void;
}

const TableHeader = ({ onSubmit }: ITableHeaderProps) => {
  const formFilter = useFormContext<IFormFilter>();
  const { t } = useTranslation();
  const { user, isExternalRole } = useAuthorization();
  const { countryOptions } = useOptionsGlobal();
  const countries = useAppSelector((state) => state?.global?.countries);
  const watchPrimaryFields = formFilter.watch('primary');
  const countryIds = watchPrimaryFields?.countryIds;
  const { data: categoryData } = useFetch<ICategory[] | null>(countryIds ? API.GET_CATEGORY(countryIds, 'PROJECT', '') : '', 'GET');
  const { userMotPicOptions } = useMotPic();
  const { companyOptions } = useCompany();
  const { data: statusData } = useFetch<any[]>('/prj/status', 'GET');
  const { data: processData } = useFetch<any[]>('/tmpl/processes', 'GET');
  const getVisaCategories = () => {
    return categoryData || [];
  };
  const isCategoryStillValid = (categoryId: string | string[] | null | undefined, selectedCountryIds: string[] | null | undefined) => {
    const normalizedCategoryId = Array.isArray(categoryId) ? categoryId[0] : categoryId;

    if (!normalizedCategoryId || !selectedCountryIds?.length) return false;

    return countries.some((country: any) => {
      const countryId = country?.id ?? country?.value ?? country?.code;
      return selectedCountryIds.includes(countryId) && (country?.categories ?? []).some((category: any) => category?.id === normalizedCategoryId);
    });
  };
  const handleFormSubmit = (values: any) => {
    onSubmit?.(values);
  };
  const totalFiltered = useMemo(() => getTotalFiltered(watchPrimaryFields), [watchPrimaryFields]);
  const renderTagSection = () => {
    const primaryKeys = [
      'countryIds',
      'categoryId',
      'picIds',
      'companyId',
      'process',
      'statuses',
      'location',
      'departureFrom',
      'departureTo',
      'createDateFrom',
      'createDateTo'
    ];
    if (!totalFiltered) return null;
    return (
      <div className='mt-4 flex flex-wrap gap-4'>
        <div className='flex flex-wrap gap-2'>
          {primaryKeys.map((key: string) => {
            const value = watchPrimaryFields[key as keyof Omit<IPrimary, 'isDraft'>];
            let label: any = value;
            if (!value) return;
            if (key === 'countryIds') {
              return (
                <>
                  {typeof value === 'object' &&
                    value?.map((countryId) => (
                      <Tag
                        key={'countryIds' + countryId}
                        closable
                        className='bg-gray2 body-400 tag-item flex items-center'
                        onClose={() => {
                          const newData = value?.filter((item) => item !== countryId);

                          const nextCountryIds = newData.length ? newData : null;
                          const nextPrimary = {
                            ...watchPrimaryFields,
                            countryIds: nextCountryIds
                          };

                          if (!isCategoryStillValid(watchPrimaryFields?.categoryId, nextCountryIds)) {
                            nextPrimary.categoryId = null;
                          }

                          formFilter.setValue('primary', nextPrimary);
                          formFilter.handleSubmit(handleFormSubmit)();
                        }}
                      >
                        {countryOptions?.find((i) => i.value === countryId)?.label}
                      </Tag>
                    ))}
                </>
              );
            } else if (key === 'picIds') {
              return (
                <>
                  {typeof value === 'object' &&
                    value?.map((picId) => {
                      const label = userMotPicOptions?.find((i) => i.value === picId)?.label;
                      return (
                        <Tag
                          key={'picId' + picId}
                          closable
                          className='bg-gray2 body-400 tag-item flex items-center'
                          onClose={() => {
                            const newData = value?.filter((item) => item !== picId);
                            formFilter.setValue('primary', {
                              ...watchPrimaryFields,
                              picIds: newData.length ? newData : null
                            });
                            formFilter.handleSubmit(handleFormSubmit)();
                          }}
                        >
                          {picId === '' ? t('project:filter:not_yet_assigned') : label}
                        </Tag>
                      );
                    })}
                </>
              );
            } else if (key === 'location') {
              const selectedLocations = Array.isArray(value) ? value : [value];
              return selectedLocations.map((location) => (
                <Tag
                  key={'location' + location}
                  closable
                  title={location}
                  className='bg-gray2 body-400 tag-item flex items-center'
                  onClose={() => {
                    const newData = selectedLocations.filter((item) => item !== location);
                    formFilter.setValue('primary', {
                      ...watchPrimaryFields,
                      location: newData.length ? newData : null
                    });
                    formFilter.handleSubmit(handleFormSubmit)();
                  }}
                >
                  {location.length > 20 ? `${location.slice(0, 20)}...` : location}
                </Tag>
              ));
            } else if (key === 'categoryId') {
              const visaCategories = getVisaCategories();
              label = visaCategories?.find((i) => i.id === value)?.name;
            } else if (key === 'companyId') {
              label = companyOptions?.find((i) => i.id === value)?.label;
            } else if (key === 'statuses') {
              if (!value) {
                return null;
              }
              const selectedStatues = Array.isArray(value) ? value : [value];
              return selectedStatues.map((statusId) => (
                <Tag
                  key={'statusId' + statusId}
                  closable
                  className='bg-gray2 body-400 tag-item flex items-center'
                  onClose={() => {
                    const newData = selectedStatues.filter((item) => item !== statusId);
                    formFilter.setValue('primary', {
                      ...watchPrimaryFields,
                      statuses: newData.length ? newData : null
                    });
                    formFilter.handleSubmit(handleFormSubmit)();
                  }}
                >
                  {statusData?.find((i) => i.id === statusId)?.name}
                </Tag>
              ));
            } else if (key === 'process') {
              label = processData?.find((i) => i.id === value)?.name;
            }
            return (
              <Tag
                key={key}
                closable
                className='bg-gray2 body-400 tag-item flex items-center'
                onClose={() => {
                  formFilter.setValue('primary', {
                    ...watchPrimaryFields,
                    [key]: null
                  });
                  formFilter.handleSubmit(handleFormSubmit)();
                }}
              >
                {label}
              </Tag>
            );
          })}
        </div>
        <Button
          size='small'
          className='tag-btn__clear-all'
          onClick={() => {
            formFilter.setValue('primary', isExternalRole ? DEFAULT_FILTER_FORM_EXTERNAL.primary : DEFAULT_FILTER_FORM.primary);
            formFilter.handleSubmit(handleFormSubmit)();
          }}
        >
          {t('button:clear_filter')}
        </Button>
      </div>
    );
  };
  return (
    <div className='mb-4'>
      <FormBaseFilter
        onSubmit={(value, isChangeView) => {
          const { picBy } = value;
          switch (picBy) {
            case TYPE_OF_TASK.ALL:
              formFilter.setValue('picBy', TYPE_OF_TASK.ALL);
              formFilter.setValue('primary', {
                ...watchPrimaryFields,
                picIds: null,
                isDraft: null
              });
              break;
            case TYPE_OF_TASK.ME:
              if (user?.id) {
                formFilter.setValue('picBy', TYPE_OF_TASK.ME);
                formFilter.setValue('primary', {
                  ...watchPrimaryFields,
                  picIds: [user?.id],
                  isDraft: true
                });
              }
              break;
            default:
              break;
          }
          onSubmit(formFilter.getValues(), isChangeView);
        }}
        searchBox={{ placeholder: DataViewer.display(t('common:PROJECT_FIELDS_SEARCH_PLACEHOLDER')) }}
        tagSection={renderTagSection()}
        primaryAction={{
          label: 'button:filter',
          name: 'primary',
          popoverContent: (
            <PrimaryFormFilter
              defaultValues={formFilter.watch('primary')}
              onSubmit={(values) => {
                const previous = formFilter.getValues();
                formFilter.reset({
                  ...previous,
                  primary: {
                    ...values,
                    departureFrom: values?.departureFrom ? formatDateTime(values?.departureFrom) : null,
                    departureTo: values?.departureTo ? formatDateTime(values?.departureTo) : null,
                    createDateFrom: values?.createDateFrom ? formatDateTime(values?.createDateFrom) : null,
                    createDateTo: values?.createDateTo ? formatDateTime(values?.createDateTo) : null
                  }
                });
                formFilter.handleSubmit(handleFormSubmit)();
              }}
              onReset={() => {}}
            />
          ),
          totalFiltered: totalFiltered || undefined
        }}
        secondaryAction={
          isExternalRole
            ? null
            : {
                options: FILTER_TASK_TYPE_OPTIONS as any[],
                name: 'picBy',
                label: TASK_LABEL[formFilter.getValues('picBy') as TYPE_OF_TASK]
              }
        }
        thirdAction={null}
      />
    </div>
  );
};

export default TableHeader;
